import * as React from 'react'
import { useCallback, useState } from 'react'
import clx from 'classnames'
import moment from 'moment'
import { useParams } from 'react-router-dom'


const DriverGroupBusinessTripNewModal = React.lazy(
  (): Promise<{ default: React.ComponentType<any> }> =>
    import('@/modules/drivers/pages/DriverGroupBusinessTripNewPage').then(
      (m: any) => ({ default: m.DriverGroupBusinessTripNewModal }),
    ),
)
const DriverBusinessTripEditModal = React.lazy(
  (): Promise<{ default: React.ComponentType<any> }> =>
    import('@/modules/drivers/pages/DriverBusinessTripEditPage').then(
      (m: any) => ({ default: m.DriverBusinessTripEditModal }),
    ),
)
import { Card } from '@/app/features/common/Card'
import { FooterWithButtons } from '../../components/FooterWithButtons'
import { Button } from '@/app/features/common/Button'
import { OneDayCalendarIcon } from '@/app/features/icons/OneDayCalendar'
import { SevenDayCalendarIcon } from '@/app/features/icons/SevenDayCalendar'
import { OneMonthCalendarIcon } from '@/app/features/icons/OneMonthCalendar'
import { ChevronDownIcon } from '@/app/features/icons/ChevronDown'
import { DelegationsGroupedTable } from './DelegationsGroupedTable'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { TripGenerationMode } from '@/app/constants/delegation/tripGenerationMode'
import { SERVICE_DATE_FORMAT } from '@/app/constants/date'
import {
  buildGenerationEvents,
  CreatorGenerationButtonMode,
  HOMELAND_COUNTRY,
  mergeAndSortGenerationEvents,
  mapButtonModeToTripGenerationMode,
} from '@/modules/creator/steps/DelegationsStep/delegationGeneration'
import {
  driverHasDelegationsInPeriod,
  fetchCreatorDelegationsFromApi,
  findApiDriverEntry,
  mapApiDriverDelegationToTableSlice,
} from '@/modules/creator/steps/DelegationsStep/creatorDelegationsSync'
import { toast } from '@/lib/toasts/api'

import { Delegation, SelectedDriver } from '@/app/types/creator'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon, faPencilSolid, faTrashSolid } from '@/lib/fa'

type DriverForTable = {
driverId: string
  name: string
  surname: string
  summary?: {
    totalTime: string
    totalOvertime: string
    totalAccommodation: number
    totalAllowances: number
  }
  delegations?: Delegation[]
}

type Props = {
  onNext: () => void
  onPrev: () => void
  selectedDrivers: SelectedDriver[]
  setSelectedDrivers?: React.Dispatch<React.SetStateAction<SelectedDriver[]>>
  startTime: string
  endTime: string
  creatorWizardState?: {
    step: number
    subStep: number
    selectedDrivers: SelectedDriver[]
  }
}

const withDriverDelegationsMeta = (
  delegations: Delegation[],
  driverId: string,
): Delegation[] => delegations.map((d) => ({ ...d, driverId }))

type OperationResultLike = {
  code?: string
  Code?: string
  type?: string | number
  Type?: string | number
  message?: string
  Message?: string
}

const isOperationResultError = (result: OperationResultLike) => {
  const typeValue = result.type ?? result.Type

  if (typeof typeValue === 'number') {
    return typeValue === 2
  }

  if (typeof typeValue === 'string') {
    return typeValue.toLowerCase() === 'error'
  }

  return false
}

class DriverGenerationError extends Error {
  codes: string[]
  rawMessages: string[]
  constructor(codes: string[], rawMessages: string[] = []) {
    super(codes.join(','))
    this.name = 'DriverGenerationError'
    this.codes = codes
    this.rawMessages = rawMessages
  }
}

const extractErrorCodesFromOperationResults = (
  results: OperationResultLike[],
): { codes: string[]; messages: string[] } => {
  const failed = results.filter(isOperationResultError)
  const codes = Array.from(
    new Set(
      failed
        .map((r) => r.code ?? r.Code)
        .filter((c): c is string => typeof c === 'string' && c.length > 0),
    ),
  )
  const messages = Array.from(
    new Set(
      failed
        .map((r) => r.message ?? r.Message)
        .filter((m): m is string => typeof m === 'string' && m.length > 0),
    ),
  )
  return { codes, messages }
}

export const DelegationsFirstSubstep = ({
  onNext,
  onPrev,
  selectedDrivers,
  setSelectedDrivers,
  startTime,
  endTime,
}: Props) => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t, tSafe } = useLocalize()
  const {
    generateTrips,
    getTmsData,
    getGpsFromTachograph,
    saveGrouped,
    getBusinessTrips,
    getBusinessTripById,
    generateAllowances,
    updateBusinessTrip,
    deleteBusinessTrips,
  } = useBusinessTripService()
  const [drivers, setDrivers] = useState<DriverForTable[]>(selectedDrivers)
  const [isGenerating, setIsGenerating] = useState(false)
  const [processingDriverIds, setProcessingDriverIds] = React.useState<
    Set<string>
  >(new Set())
  const [generationErrors, setGenerationErrors] = React.useState<
    Record<string, string>
  >({})
  const [isPendingSectionOpen, setIsPendingSectionOpen] = React.useState(true)
  const [isCompletedSectionOpen, setIsCompletedSectionOpen] =
    React.useState(false)
  const [editingDriverId, setEditingDriverId] = React.useState<string | null>(
    null,
  )
  const [editingTrip, setEditingTrip] = React.useState<{
    driverId: string
    tripId: string
  } | null>(null)

  const translateErrorCode = useCallback(
    (code: string) => {
      const key = `errorCode.${code}`
      const translated = t(key)
      return translated && translated !== key ? translated : code
    },
    [t],
  )

  const setDriverError = useCallback((driverId: string, message: string) => {
    setGenerationErrors((prev) => ({ ...prev, [driverId]: message }))
  }, [])

  const clearDriverError = useCallback((driverId: string) => {
    setGenerationErrors((prev) => {
      if (!(driverId in prev)) return prev
      const next = { ...prev }
      delete next[driverId]
      return next
    })
  }, [])

  const removeDriverFromSettlement = useCallback(
    (driverId: string) => {
      setDrivers((prev) => prev.filter((d) => d.driverId !== driverId))
      clearDriverError(driverId)
      setProcessingDriverIds((prev) => {
        if (!prev.has(driverId)) return prev
        const next = new Set(prev)
        next.delete(driverId)
        return next
      })
      inProgressDriversRef.current.delete(driverId)
      setSelectedDrivers?.((prev) =>
        prev.filter((d) => d.driverId !== driverId),
      )
    },
    [clearDriverError, setSelectedDrivers],
  )

  React.useEffect(() => {
    if (selectedDrivers.length === 0) return undefined
    setDrivers((prev) => (prev.length === 0 ? selectedDrivers : prev))
    return undefined
  }, [selectedDrivers])


  React.useEffect(() => {
    if (!companyId) return undefined

    const driverIds = selectedDrivers
      .map((d) => Number(d.driverId))
      .filter((id) => Number.isInteger(id))
    if (!driverIds.length) return undefined

    let cancelled = false
    void (async () => {
      try {
        const apiDrivers = await fetchCreatorDelegationsFromApi({
          companyId,
          startTime,
          endTime,
          driverIds,
        })
        if (cancelled) return
        setDrivers((prev) =>
          prev.map((driver) => {
            const apiDriver = findApiDriverEntry(apiDrivers, driver.driverId)
            const { delegations, summary } =
              mapApiDriverDelegationToTableSlice(apiDriver)
            return {
              ...driver,
              summary,
              delegations: withDriverDelegationsMeta(
                delegations,
                driver.driverId,
              ),
            }
          }),
        )
      } catch (err) {
        console.error('Initial delegations probe failed', err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [companyId, selectedDrivers, startTime, endTime])

  const inProgressDriversRef = React.useRef(new Set<string>())
  const [expandedRow, setExpandedRow] = React.useState<string>()

  const toggleRow = (driverId: string) => {
    setExpandedRow((prev) => (prev === driverId ? undefined : driverId))
  }


  React.useEffect(() => {
    if (!expandedRow) return
    const expandedDriver = drivers.find((d) => d.driverId === expandedRow)
    if (
      !expandedDriver ||
      !driverHasDelegationsInPeriod(expandedDriver.delegations)
    ) {
      setExpandedRow(undefined)
    }
  }, [drivers, expandedRow])

  const getDelegationsData = useCallback(
    async (driverId: string, forceFetch = false) => {
      if (!companyId) return

      const normalizedDriverId = Number(driverId)
      if (!Number.isInteger(normalizedDriverId)) return

      const currentDriver = drivers.find(
        (driver) => driver.driverId === driverId,
      )
      if (currentDriver?.delegations !== undefined && !forceFetch) return

      const apiDrivers = await fetchCreatorDelegationsFromApi({
        companyId,
        startTime,
        endTime,
        driverIds: [normalizedDriverId],
      })

      const apiDriver = findApiDriverEntry(apiDrivers, driverId)
      const { delegations: mappedDelegations, summary } =
        mapApiDriverDelegationToTableSlice(apiDriver)

      setDrivers((prev) =>
        prev.map((driver) =>
          driver.driverId === driverId
            ? {
                ...driver,
                summary,
                delegations: mappedDelegations,
              }
            : driver,
        ),
      )
    },
    [companyId, drivers, endTime, startTime, t],
  )

  const runGenerationForDriver = useCallback(
    async (
      driverId: string,
      mode: CreatorGenerationButtonMode,
      options?: { skipDelegationsPreflight?: boolean },
    ) => {
      if (!companyId) return

      const normalizedDriverId = Number(driverId)
      if (!Number.isInteger(normalizedDriverId)) return
      if (inProgressDriversRef.current.has(driverId)) return

      inProgressDriversRef.current.add(driverId)
      setProcessingDriverIds((prev) => new Set(prev).add(driverId))
      clearDriverError(driverId)

      try {
        if (!options?.skipDelegationsPreflight) {
          const apiDrivers = await fetchCreatorDelegationsFromApi({
            companyId,
            startTime,
            endTime,
            driverIds: [normalizedDriverId],
          })
          const apiDriver = findApiDriverEntry(apiDrivers, driverId)
          const { delegations: existingDelegations, summary } =
            mapApiDriverDelegationToTableSlice(apiDriver)

          if (driverHasDelegationsInPeriod(existingDelegations)) {
                  setDrivers((prev) =>
              prev.map((driver) =>
                driver.driverId === driverId
                  ? {
                      ...driver,
                      summary,
                      delegations: withDriverDelegationsMeta(
                        existingDelegations,
                        driverId,
                      ),
                    }
                  : driver,
              ),
            )
            return
          }

              setDrivers((prev) =>
            prev.map((driver) =>
              driver.driverId === driverId
                ? {
                    ...driver,
                    summary,
                    delegations: withDriverDelegationsMeta(
                      existingDelegations,
                      driverId,
                    ),
                  }
                : driver,
            ),
          )
        }

        const [tmsData, bordersData] = await Promise.all([
          getTmsData({
            companyId,
            driverId: normalizedDriverId,
            startTime,
            endTime,
          }),
          getGpsFromTachograph({
            companyId,
            driverId: normalizedDriverId,
            startTime,
            endTime,
          }),
        ])

        const generationEvents = buildGenerationEvents({
          tmsData: tmsData ?? [],
          bordersData: bordersData ?? [],
        })

        const [generatedEvents, generatedForeignEvents] = await Promise.all([
          generateTrips({
            companyId,
            driverId: normalizedDriverId,
            defaultStartDate: startTime,
            defaultEndDate: endTime,
            events: generationEvents,
            generationMode: mapButtonModeToTripGenerationMode(mode),
          }),
          generateTrips({
            companyId,
            driverId: normalizedDriverId,
            defaultStartDate: startTime,
            defaultEndDate: endTime,
            events: generationEvents,
            generationMode: TripGenerationMode.Foreign,
          }),
        ])

        const eventsToSave = mergeAndSortGenerationEvents({
          importedEvents: generationEvents,
          generatedEvents: [
            ...(generatedEvents ?? []),
            ...(generatedForeignEvents ?? []),
          ],
        })

        if (eventsToSave.length === 0) {
          throw new DriverGenerationError(
            ['NO_EVENTS_TO_GENERATE'],
            [
              tSafe(
                'creator.delegations.error.noTachoData',
                'Brak danych z tachografu w wybranym okresie.',
              ),
            ],
          )
        }

        const tripsBeforeSave = await getBusinessTrips({
          companyId,
          drivers: [normalizedDriverId],
          startTime,
          endTime,
        })
        const existingTripIds = new Set(
          tripsBeforeSave.map((trip) => String(trip.id)),
        )

        const saveResult = await saveGrouped({
          companyId,
          id: '0',
          driverId: normalizedDriverId,
          defaultStartDate: startTime,
          country: HOMELAND_COUNTRY,
          events: eventsToSave,
          force: false,
          withPreviousExchangeRateDate: false,
          forceCopyToCoDriver: true,
          copyToCoDriver: false,
          checkedTripCoDrivers: [],
        })

        const operationResults: OperationResultLike[] = Array.isArray(
          saveResult,
        )
          ? (saveResult as OperationResultLike[])
          : []
        const hasErrors = operationResults.some((result) =>
          isOperationResultError(result),
        )

        if (hasErrors) {
          const { codes, messages } =
            extractErrorCodesFromOperationResults(operationResults)
          throw new DriverGenerationError(
            codes.length > 0 ? codes : ['UNKNOWN_ERROR'],
            messages,
          )
        }

        const tripsAfterSave = await getBusinessTrips({
          companyId,
          drivers: [normalizedDriverId],
          startTime,
          endTime,
        })
        const createdTrips = tripsAfterSave.filter(
          (trip) => !existingTripIds.has(String(trip.id)),
        )

        const costsGenerationFailures: string[] = []
        for (const trip of createdTrips) {
          try {
            const details = await getBusinessTripById({
              companyId,
              tripId: trip.id,
            })
            if (!details) {
              continue
            }

            const exchangeRateDate = details.exchangeRateDate
              ? moment(details.exchangeRateDate).format(SERVICE_DATE_FORMAT)
              : moment().format(SERVICE_DATE_FORMAT)

            const allowancesResult = await generateAllowances({
              companyId,
              driverId: normalizedDriverId,
              exchangeRateDate,
              country:
                details.country ??
                details.destinationCountry ??
                HOMELAND_COUNTRY,
              events: details.events ?? [],
              alimentations: details.alimentations ?? [],
              invoices: details.invoices ?? [],
              accomodations:
                details.accomodations ?? details.accommodations ?? [],
              stopDownloadingRates: details.stopDownloadingRates ?? false,
            })

            await updateBusinessTrip({
              companyId,
              tripId: trip.id,
              driverId: normalizedDriverId,
              data: {
                ...details,
                allowances:
                  allowancesResult?.allowances ??
                  allowancesResult?.Allowances ??
                  details.allowances ??
                  [],
                invoices:
                  allowancesResult?.invoices ??
                  allowancesResult?.Invoices ??
                  details.invoices ??
                  [],
                alimentations:
                  allowancesResult?.alimentations ??
                  allowancesResult?.Alimentations ??
                  details.alimentations ??
                  [],
              },
            })
          } catch (error) {
            console.error(
              `Creator costs generation failed for trip ${String(trip.id)}`,
              error,
            )
            costsGenerationFailures.push(String(trip.number ?? trip.id))
          }
        }

        if (costsGenerationFailures.length) {
          toast.warning({
            text: `${tSafe('creator.delegations.error.costGenerationFailed', 'Nie udało się wygenerować kosztów dla')}: ${costsGenerationFailures.join(', ')}`,
          })
        }

        await getDelegationsData(driverId, true)
      } catch (err) {
        console.error(
          `Creator delegation generation failed for driver ${driverId}`,
          err,
        )
        if (err instanceof DriverGenerationError) {
          const translated = err.codes
            .map((code) => translateErrorCode(code))
            .filter(Boolean)
          const message =
            translated.length > 0
              ? translated.join(', ')
              : err.rawMessages.length > 0
                ? err.rawMessages.join(', ')
                : t('error.generate')
          setDriverError(driverId, message)
        } else {
          setDriverError(driverId, t('error.generate'))
        }
     
      } finally {
        inProgressDriversRef.current.delete(driverId)
        setProcessingDriverIds((prev) => {
          const next = new Set(prev)
          next.delete(driverId)
          return next
        })
      }
    },
    [
      clearDriverError,
      companyId,
      endTime,
      generateTrips,
      generateAllowances,
      getBusinessTripById,
      getBusinessTrips,
      getDelegationsData,
      getGpsFromTachograph,
      getTmsData,
      saveGrouped,
      setDriverError,
      startTime,
      t,
      translateErrorCode,
      updateBusinessTrip,
      tSafe,
    ],
  )

  const generateForDriver = useCallback(
    async (driverId: string, mode: CreatorGenerationButtonMode) => {
      if (isGenerating) return

      setIsGenerating(true)
      try {
        await runGenerationForDriver(driverId, mode)
      } finally {
        setIsGenerating(false)
      }
    },
    [isGenerating, runGenerationForDriver],
  )

  const renderDetailRowActions = useCallback(
    (delegation: Delegation, driverId: string) => {
      const tripId = delegation.tripId
      if (!companyId || !tripId || !driverId) {
        return <div className="tw-flex tw-items-center" />
      }
      const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation()
        try {
          await deleteBusinessTrips({ companyId, ids: [Number(tripId)] })
          await getDelegationsData(String(driverId), true)
        } catch (err) {
          console.error('Delete delegation failed', err)
          toast.error({ text: t('error.general') })
        }
      }
      return (
        <div className="tw-flex tw-items-center tw-gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setEditingTrip({ driverId, tripId: String(tripId) })
            }}
            className="tw-text-sm tw-text-primary hover:tw-underline"
          >
            <FaIcon icon={faPencilSolid} className="tw-text-primary" />
          </button>
          <FaIcon
            icon={faTrashSolid}
            className="tw-text-red-500 tw-cursor-pointer"
            onClick={handleDelete}
          />
        </div>
      )
    },
    [companyId, deleteBusinessTrips, getDelegationsData, t],
  )

  const handleCloseEditTripModal = useCallback(
    (saved: boolean) => {
      const closedDriverId = editingTrip?.driverId
      setEditingTrip(null)
      if (saved && closedDriverId) {
        void getDelegationsData(closedDriverId, true)
      }
    },
    [editingTrip, getDelegationsData],
  )

  const renderErrorAction = useCallback(
    (driverId: string) => {
      if (!companyId) return null
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setEditingDriverId(driverId)
          }}
          className="tw-rounded tw-border tw-border-primary tw-bg-primary/5 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-primary tw-transition-colors hover:tw-bg-primary/10"
        >
          {tSafe('creator.delegations.edit', 'Edytuj delegację')}
        </button>
      )
    },
    [companyId, tSafe],
  )

  const handleCloseEditModal = useCallback(
    (saved: boolean) => {
      const closedDriverId = editingDriverId
      setEditingDriverId(null)
      if (!closedDriverId) return
      if (saved) {

        clearDriverError(closedDriverId)
    
        void getDelegationsData(closedDriverId, true)
      }
    },
    [editingDriverId, getDelegationsData, clearDriverError],
  )

  const allDriversHaveDelegations = React.useMemo(() => {
    return drivers.every((driver) =>
      driverHasDelegationsInPeriod(driver.delegations),
    )
  }, [drivers])

  const pendingDrivers = React.useMemo(
    () =>
      drivers.filter(
        (driver) => !driverHasDelegationsInPeriod(driver.delegations),
      ),
    [drivers],
  )

  const completedDrivers = React.useMemo(
    () =>
      drivers.filter((driver) =>
        driverHasDelegationsInPeriod(driver.delegations),
      ),
    [drivers],
  )

  React.useEffect(() => {
    if (pendingDrivers.length > 0 && completedDrivers.length === 0) {
    setIsPendingSectionOpen(true)
    }
  }, [pendingDrivers.length, completedDrivers.length])

  React.useEffect(() => {
    if (completedDrivers.length > 0 && pendingDrivers.length === 0) {
      setIsCompletedSectionOpen(true)
    }
  }, [pendingDrivers.length, completedDrivers.length])

  const generateAll = useCallback(
    async (mode: CreatorGenerationButtonMode) => {
      if (isGenerating || !companyId) return

      const numericIds = drivers
        .map((d) => Number(d.driverId))
        .filter((id) => Number.isInteger(id))

      if (!numericIds.length) return

      setIsGenerating(true)
      try {
        let apiDrivers: any[] = []
        let preflightFailed = false
        try {
          apiDrivers = await fetchCreatorDelegationsFromApi({
            companyId,
            startTime,
            endTime,
            driverIds: numericIds,
          })
        } catch (err) {
          console.error('Creator batch preflight fetch failed', err)
          preflightFailed = true
        }

        if (!preflightFailed) {
          setDrivers((prev) =>
            prev.map((driver) => {
              const apiDriver = findApiDriverEntry(apiDrivers, driver.driverId)
              const { delegations: mappedDelegations, summary } =
                mapApiDriverDelegationToTableSlice(apiDriver)
              return {
                ...driver,
                summary,
                delegations: withDriverDelegationsMeta(
                  mappedDelegations,
                  driver.driverId,
                ),
              }
            }),
          )
        }


        const driversToGenerate = preflightFailed
          ? drivers
          : drivers.filter((driver) => {
              const slice = mapApiDriverDelegationToTableSlice(
                findApiDriverEntry(apiDrivers, driver.driverId),
              )
              return !driverHasDelegationsInPeriod(slice.delegations)
            })

        setProcessingDriverIds((prev) => {
          const next = new Set(prev)
          driversToGenerate.forEach((d) => next.add(d.driverId))
          return next
        })

        for (const driver of driversToGenerate) {
   
          await runGenerationForDriver(driver.driverId, mode, {
            skipDelegationsPreflight: !preflightFailed,
          })
        }
      } finally {
        setIsGenerating(false)
      }
    },
    [
      companyId,
      drivers,
      isGenerating,
      runGenerationForDriver,
      startTime,
      endTime,
    ],
  )

  return (
    <Card
      className={clx(
        'tw-inline-block tw-h-full tw-max-w-full tw-overflow-x-auto tw-bg-base-100 tw-pb-3',
        {
          'tw-rounded-lg tw-pt-0': allDriversHaveDelegations,
        },
      )}
      classNameContent="!tw-px-0 !tw-pt-0 tw-overflow-x-auto tw-flex tw-flex-col tw-h-full"
      paddingTop={false}
    >
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {pendingDrivers.length > 0 && (
        <section
          className={clx(
            'first:tw-mt-0 tw-flex tw-flex-col tw-border-b tw-border-primary/30',
            isPendingSectionOpen && 'tw-min-h-0 tw-flex-1',
          )}
        >
          <div className="tw-flex tw-w-full tw-shrink-0 tw-items-center tw-gap-2 tw-bg-secondary tw-px-4 tw-py-2.5 tw-text-sm">
            <button
              type="button"
              onClick={() => setIsPendingSectionOpen((v) => !v)}
              aria-expanded={isPendingSectionOpen}
              className="tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-text-left hover:tw-brightness-95"
            >
              <ChevronDownIcon
                className={`tw-h-3 tw-w-3 tw-shrink-0 tw-text-content-base tw-transition-transform ${
                  isPendingSectionOpen ? '' : '-tw-rotate-90'
                }`}
              />
              <span className="tw-font-semibold">{pendingDrivers.length}</span>
              <span>{t('creator.drivers-pending-generation')}</span>
            </button>
            <div className="tw-flex tw-items-center tw-gap-3">
              <span className="tw-text-xs">
                {t('creator.generate-delegations-all')}:
              </span>
              <Button
                size="sm"
                variant="link"
                text={t('creator.1-day')}
                onClick={() => generateAll('1-day')}
                disabled={isGenerating}
              >
                <OneDayCalendarIcon />
              </Button>
              <Button
                size="sm"
                variant="link"
                text={t('creator.7-day')}
                onClick={() => generateAll('7-day')}
                disabled={isGenerating}
              >
                <SevenDayCalendarIcon />
              </Button>
              <Button
                size="sm"
                variant="link"
                text={t('creator.30-day')}
                onClick={() => generateAll('30-day')}
                disabled={isGenerating}
              >
                <OneMonthCalendarIcon />
              </Button>
            </div>
          </div>
          {isPendingSectionOpen && (
            <DelegationsGroupedTable
              drivers={pendingDrivers}
              expandedRow={expandedRow}
              toggleRow={toggleRow}
              onGenerateForDriver={generateForDriver}
              processingDriverIds={processingDriverIds}
              generationErrors={generationErrors}
              onRemoveFromSettlement={removeDriverFromSettlement}
              renderErrorAction={renderErrorAction}
              renderDetailRowActions={renderDetailRowActions}
            />
          )}
        </section>
      )}

      {completedDrivers.length > 0 && (
        <section
          className={clx(
            'first:tw-mt-0 tw-flex tw-flex-col tw-border-b tw-border-primary/30',
            isCompletedSectionOpen && 'tw-min-h-0 tw-flex-1',
          )}
        >
          <button
            type="button"
            onClick={() => setIsCompletedSectionOpen((v) => !v)}
            aria-expanded={isCompletedSectionOpen}
            className="tw-flex tw-w-full tw-shrink-0 tw-items-center tw-gap-2 tw-bg-secondary tw-px-4 tw-py-2.5 tw-text-left tw-text-sm hover:tw-brightness-95"
          >
            <ChevronDownIcon
              className={`tw-h-3 tw-w-3 tw-shrink-0 tw-text-content-base tw-transition-transform ${
                isCompletedSectionOpen ? '' : '-tw-rotate-90'
              }`}
            />
            <span className="tw-font-semibold">{completedDrivers.length}</span>
            <span>{t('creator.drivers-with-delegations')}</span>
          </button>
          {isCompletedSectionOpen && (
            <DelegationsGroupedTable
              drivers={completedDrivers}
              expandedRow={expandedRow}
              toggleRow={toggleRow}
              onGenerateForDriver={generateForDriver}
              processingDriverIds={processingDriverIds}
              generationErrors={generationErrors}
              onRemoveFromSettlement={removeDriverFromSettlement}
              renderErrorAction={renderErrorAction}
              renderDetailRowActions={renderDetailRowActions}
            />
          )}
        </section>
      )}
      </div>

      <FooterWithButtons
        onNext={onNext}
        onPrev={onPrev}
        className="tw-shrink-0 tw-border-t tw-border-base-300"
        isNextDisabled={!allDriversHaveDelegations}
      />

      {editingDriverId && companyId && (
        <React.Suspense fallback={null}>
          <DriverGroupBusinessTripNewModal
            companyId={companyId}
            driverId={editingDriverId}
            from={startTime}
            to={endTime}
            onClose={handleCloseEditModal}
          />
        </React.Suspense>
      )}

      {editingTrip && companyId && (
        <React.Suspense fallback={null}>
          <DriverBusinessTripEditModal
            companyId={companyId}
            driverId={editingTrip.driverId}
            tripId={editingTrip.tripId}
            onClose={handleCloseEditTripModal}
          />
        </React.Suspense>
      )}
    </Card>
  )
}
